/**
 * SignupPage.tsx — Supabase Auth integration (Task #34).
 * Uses supabase.signUp; server provisions the internal user record on first
 * authenticated API call via requireAuth middleware.
 *
 * Task #69: Cloudflare Turnstile invisible security gate (Phase 1).
 * Task #70: Boarding Pass Clearance™ (Phase 2) — the branded puzzle overlay
 *   wraps useTurnstile().execute() and verifyTurnstileToken() internally.
 *   SignupPage never touches Turnstile directly; it renders
 *   <BoardingPassClearance> which handles the full verification flow and
 *   calls onVerified() on success, then signUp() proceeds.
 */

import { useState, useRef } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { User, Lock, Eye, EyeOff, Globe, UserCheck, Loader2 } from 'lucide-react';
import WorkRoamerBrand from '@/components/WorkRoamerBrand';
import PrivacyCommitmentPanel from '@/components/legal/PrivacyCommitmentPanel';
import TrustOverviewModal from '@/components/legal/TrustOverviewModal';
import { recordTermsAcceptance } from '@/lib/termsVersion';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import BoardingPassClearance from '@/components/auth/BoardingPassClearance';

const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', background: disabled ? 'rgba(6,15,31,0.6)' : '#060f1f',
  border: '1.5px solid rgba(59,130,246,0.22)', borderRadius: '8px',
  color: disabled ? '#4b6fa8' : '#e2e8f0', fontSize: '14px',
  padding: '13px 14px 13px 44px', outline: 'none', boxSizing: 'border-box' as const,
  fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'text',
  transition: 'border-color 0.2s, background 0.2s',
});

const labelStyle: React.CSSProperties = {
  display: 'block', color: '#3b82f6', fontSize: '10.5px', fontWeight: '700',
  letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: '8px',
};

function GlowDivider() {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.5))' }} />
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', margin: '0 3px', flexShrink: 0, boxShadow: '0 0 8px 2px rgba(59,130,246,0.7), 0 0 18px rgba(59,130,246,0.35)' }} />
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(59,130,246,0.5), transparent)' }} />
    </div>
  );
}

function humaniseError(raw: string): string {
  const s = raw.toLowerCase();
  if (s.includes('already registered') || s.includes('user already registered') || s.includes('already in use'))
    return 'This email address is already registered. Try signing in instead.';
  if (s.includes('invalid email') || s.includes('invalid input'))
    return 'Please enter a valid email address.';
  if (s.includes('password') && s.includes('least'))
    return 'Password must be at least 6 characters.';
  if (s.includes('too many') || s.includes('rate limit'))
    return 'Too many attempts. Please wait a few minutes.';
  return "We couldn't create your account right now. Please try again.";
}

export default function SignupPage() {
  const [displayName, setDisplayName] = useState('');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [showConf,    setShowConf]    = useState(false);
  const [terms,       setTerms]       = useState(false);
  const [showTrust,   setShowTrust]   = useState(false);
  const [loading,             setLoading]             = useState(false);
  // emailSentFallback: only shown when Supabase project requires email
  // confirmation before password sign-in (strict mode). Under the delayed-
  // verification architecture this is a soft fallback, not the primary flow.
  const [emailSentFallback,   setEmailSentFallback]   = useState(false);
  const [registeredEmail,     setRegisteredEmail]     = useState('');
  const [error,               setError]               = useState<string | null>(null);
  const [showBoardingPass,    setShowBoardingPass]    = useState(false);

  const [, setLocation]  = useLocation();
  const queryClient      = useQueryClient();
  const { signUp, signIn } = useSupabaseAuth();

  const displayNameRef = useRef<HTMLInputElement>(null);
  const emailRef       = useRef<HTMLInputElement>(null);
  const passwordRef    = useRef<HTMLInputElement>(null);
  const confirmRef     = useRef<HTMLInputElement>(null);
  const termsRef       = useRef<HTMLInputElement>(null);

  const validate = (): string | null => {
    if (!displayName.trim()) { displayNameRef.current?.focus(); return 'Display name is required.'; }
    if (!email.trim())       { emailRef.current?.focus();       return 'Email address is required.'; }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) { emailRef.current?.focus(); return 'Please enter a valid email address.'; }
    if (password.length < 6) { passwordRef.current?.focus();   return 'Password must be at least 6 characters.'; }
    if (password !== confirm) { confirmRef.current?.focus();   return 'Passwords do not match.'; }
    if (!terms) { termsRef.current?.focus();                   return 'You must accept the Terms of Service to continue.'; }
    return null;
  };

  // ── Step 1: validate form → show Boarding Pass puzzle ────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    // Show the Boarding Pass Clearance™ overlay.
    // It runs useTurnstile().execute() + verifyTurnstileToken() internally
    // and calls onVerified() when both succeed.
    setShowBoardingPass(true);
  };

  // ── Step 2: called by BoardingPassClearance after Turnstile verified ──────
  //
  // TASK 71 — Delayed Email Verification Architecture:
  // Turnstile has already confirmed this is a legitimate human. WorkRoamer
  // grants immediate access so users enter the product without friction.
  // Email verification becomes a friendly post-onboarding reminder (Shell.tsx),
  // not a blocker. The verification email is still sent by Supabase.
  const handleBoardingPassVerified = async () => {
    setShowBoardingPass(false);
    setLoading(true);
    try {
      // Step 2a: Create the account (Supabase sends the verification email).
      const { error: authError } = await signUp(email.trim(), password, displayName.trim());
      if (authError) {
        setError(humaniseError(authError.message ?? ''));
        return;
      }
      // Record terms acceptance for audit trail.
      recordTermsAcceptance();

      // Step 2b: Sign in immediately — delayed-verification fast path.
      // Under the new architecture users proceed to onboarding without
      // waiting for an email click. Shell shows a friendly reminder instead.
      const { error: signInError } = await signIn(email.trim(), password);
      if (!signInError) {
        // Session established — set fresh-signup flag so Shell fires the
        // onboarding wizard, then navigate. useGetMe auto-provisions the
        // internal user record on first authenticated call.
        try { sessionStorage.setItem('nomad:fresh-signup', '1'); } catch { /* ignore */ }
        queryClient.clear();
        setLocation('/home');
        return;
      }

      // Step 2c: Fallback — Supabase project has strict email confirmation
      // enabled (signInWithPassword rejected for unverified accounts).
      // The email was still sent; show a soft (non-blocking) confirmation.
      // The user can sign in after clicking the link, or try again now.
      setRegisteredEmail(email.trim());
      setEmailSentFallback(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBoardingPassError = (msg: string) => {
    setShowBoardingPass(false);
    setError(msg);
  };

  // ── Email-sent fallback screen ────────────────────────────────────────────
  // Shown ONLY when Supabase project has strict email confirmation enabled and
  // signInWithPassword rejects unverified accounts (rare / edge-case).
  // Primary flow: signIn() succeeds → user navigates to /home immediately.
  if (emailSentFallback) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)', padding: '20px 16px', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ width: '100%', maxWidth: 420, boxSizing: 'border-box' as const, padding: 'clamp(28px,6vw,44px) clamp(20px,5.5vw,36px)', background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)', borderRadius: 20, border: '1.5px solid rgba(59,130,246,0.40)', boxShadow: ['0 0 40px rgba(37,99,235,0.25)', '0 0 80px rgba(37,99,235,0.10)', 'inset 0 1px 0 rgba(255,255,255,0.04)'].join(', '), display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 0 }}>
          <WorkRoamerBrand width={260} style={{ marginBottom: 24 }} />
          <div style={{ width: 72, height: 72, borderRadius: 18, background: 'rgba(59,130,246,0.12)', border: '1.5px solid rgba(59,130,246,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', textAlign: 'center', letterSpacing: '0.01em', marginBottom: 10 }}>Account created!</div>
          <div style={{ fontSize: 13, color: '#8fa8c8', textAlign: 'center', lineHeight: 1.7, marginBottom: 4 }}>A verification link has been sent to:</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.22)', borderRadius: 8, padding: '8px 16px', marginBottom: 20, wordBreak: 'break-all' as const, textAlign: 'center' }}>{registeredEmail}</div>
          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center', lineHeight: 1.7, marginBottom: 24, padding: '0 8px' }}>
            Click the link in the email to activate your account, or try signing in directly if your provider allows immediate access.
          </div>
          <GlowDivider />
          <div style={{ marginTop: 20, width: '100%', textAlign: 'center' }}>
            <p style={{ color: '#3a5070', fontSize: '11.5px', margin: 0 }}>
              Ready to sign in?{' '}
              <a href="/login" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>Go to login</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Boarding Pass Clearance™ overlay (Phase 2) ─────────────────── */}
      {showBoardingPass && (
        <BoardingPassClearance
          onVerified={handleBoardingPassVerified}
          onError={handleBoardingPassError}
          onClose={() => setShowBoardingPass(false)}
        />
      )}

      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)', padding: '20px 16px', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ width: '100%', maxWidth: '430px', boxSizing: 'border-box' as const, padding: 'clamp(24px,6vw,40px) clamp(16px,5.5vw,32px) clamp(20px,4vw,28px)', overflow: 'hidden' as const, background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)', borderRadius: '20px', border: '1.5px solid rgba(59,130,246,0.45)', boxShadow: ['0 0 0 1px rgba(59,130,246,0.08)', '0 0 40px rgba(37,99,235,0.30)', '0 0 80px rgba(37,99,235,0.12)', 'inset 0 1px 0 rgba(255,255,255,0.04)'].join(', '), display: 'flex', flexDirection: 'column' as const, alignItems: 'center' }}>
          <WorkRoamerBrand width={300} style={{ marginBottom: 4 }} />
          <p style={{ margin: '6px 0 4px', color: '#8fa8c8', fontSize: '13px', textAlign: 'center', fontWeight: 600 }}>Begin your journey as a WorkRoamer.</p>

          <div style={{ margin: '0 0 14px', padding: '10px 14px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.14)', borderRadius: 8, display: 'flex', flexDirection: 'column' as const, gap: 6, width: '100%' }}>
            {['🛂  Your identity — one profile, every mission', '💰  Income tracking — built for nomadic earners', '🌎  Opportunity engine — ranked for your profile', '🛰  Mission Control — your dashboard for daily work'].map(line => (
              <div key={line} style={{ color: 'rgba(143,168,200,0.75)', fontSize: '11px', letterSpacing: '0.02em', fontFamily: 'monospace' }}>{line}</div>
            ))}
          </div>

          <GlowDivider />

          <form onSubmit={handleSubmit} noValidate style={{ width: '100%', marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Display Name */}
            <div>
              <label style={labelStyle}>Display Name</label>
              <div style={{ position: 'relative' }}>
                <UserCheck size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input ref={displayNameRef} type="text" autoComplete="name" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="How should we call you?" disabled={loading} style={inputStyle(loading)}
                  onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')} />
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input ref={emailRef} type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" disabled={loading} style={inputStyle(loading)}
                  onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input ref={passwordRef} type={showPass ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" disabled={loading} style={{ ...inputStyle(loading), padding: '13px 44px 13px 44px' }}
                  onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')} />
                <button type="button" onClick={() => { if (!loading) setShowPass(v => !v); }} disabled={loading}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#4b6fa8', cursor: loading ? 'not-allowed' : 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input ref={confirmRef} type={showConf ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm your password" disabled={loading} style={{ ...inputStyle(loading), padding: '13px 44px 13px 44px' }}
                  onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')} />
                <button type="button" onClick={() => { if (!loading) setShowConf(v => !v); }} disabled={loading}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#4b6fa8', cursor: loading ? 'not-allowed' : 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showConf ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Terms */}
            <PrivacyCommitmentPanel />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px', color: '#8fa8c8' }}>
              <input ref={termsRef} type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} disabled={loading} style={{ accentColor: '#3b82f6', width: 16, height: 16, flexShrink: 0, marginTop: 1 }} />
              <span>
                I agree to the{' '}
                <button type="button" onClick={() => setShowTrust(true)} style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', fontFamily: 'inherit' }}>
                  Terms of Service &amp; Privacy Policy
                </button>
              </span>
            </label>

            {error && <p style={{ color: '#f87171', fontSize: '12px', textAlign: 'center', margin: 0 }}>{error}</p>}

            <button type="submit" disabled={loading} style={{
              width: '100%', background: loading ? 'rgba(37,99,235,0.45)' : 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 45%, #3b82f6 100%)',
              border: 'none', borderRadius: '8px', color: '#ffffff', fontSize: '13px', fontWeight: '800',
              letterSpacing: '0.16em', textTransform: 'uppercase' as const, padding: '16px 24px',
              cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: '4px',
              boxShadow: loading ? 'none' : '0 4px 18px rgba(37,99,235,0.45)', fontFamily: 'inherit', opacity: loading ? 0.65 : 1,
            }}>
              {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Globe size={16} />}
              {loading ? 'Creating account…' : 'Create Roamer Account'}
            </button>
          </form>

          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 14px' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(59,130,246,0.2)' }} />
            <Globe size={15} style={{ color: '#3b6cb0', flexShrink: 0 }} />
            <div style={{ flex: 1, height: 1, background: 'rgba(59,130,246,0.2)' }} />
          </div>

          <p style={{ color: '#3a5070', fontSize: '11.5px', margin: '0 0 18px', textAlign: 'center' }}>
            Already have an account?{' '}
            <a href="/login" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Sign in</a>
          </p>
          <div style={{ borderTop: '1px solid rgba(59,130,246,0.12)', width: '100%', paddingTop: '16px' }}>
            <p style={{ color: '#2a3d58', fontSize: '11px', margin: 0, textAlign: 'center' }}>
              © 2026 <strong style={{ color: '#3a5070' }}>WorkRoamer™</strong>{' '}•{' '}Built for people who work from anywhere
            </p>
          </div>
        </div>
      </div>

      {showTrust && <TrustOverviewModal onClose={() => setShowTrust(false)} />}
    </>
  );
}
